/**
 * FiscalReceipt - FRCS VMS Compliant Fiscal Receipt Component
 * Displays fiscal invoice with QR code for Fiji VAT compliance
 */
import { useState, useEffect, useRef } from 'react';
import { QrCode, Printer, Download, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/utils/apiClient';
import { toast } from 'sonner';

// Fiscal Receipt Display Component
export const FiscalReceiptDisplay = ({ fiscalInvoice, onPrint, showPrintButton = true }) => {
  if (!fiscalInvoice) return null;

  const isNonFiscal = ['PS', 'PR', 'CS', 'CR', 'TS', 'TR'].includes(fiscalInvoice.invoice_type);

  return (
    <div className="bg-white text-black font-mono text-xs p-4 max-w-[300px] mx-auto border border-gray-300 shadow-lg" data-testid="fiscal-receipt">
      {/* Title Line */}
      <div className="text-center border-t-2 border-b-2 border-black py-1 mb-2">
        <div className="font-bold">FISCAL INVOICE</div>
      </div>

      {/* Header - Taxpayer Info */}
      <div className="mb-2">
        <div className="font-bold">{fiscalInvoice.tin}</div>
        <div>{fiscalInvoice.business_name}</div>
        {fiscalInvoice.trade_name && fiscalInvoice.trade_name !== fiscalInvoice.business_name && (
          <div>{fiscalInvoice.trade_name}</div>
        )}
        <div>{fiscalInvoice.address}</div>
        {fiscalInvoice.district && <div>{fiscalInvoice.district}</div>}
      </div>

      {/* Cashier */}
      <div className="mb-2">
        <span>Cashier: </span>
        <span className="font-semibold">{fiscalInvoice.cashier}</span>
      </div>

      {/* Buyer (if applicable) */}
      {fiscalInvoice.buyer_tin && (
        <div className="mb-2">
          <span>Buyer: </span>
          <span className="font-semibold">{fiscalInvoice.buyer_tin}</span>
        </div>
      )}

      {/* POS Info */}
      <div className="text-[10px] mb-2">
        <div>POS Number: {fiscalInvoice.pos_number}</div>
        {fiscalInvoice.reference_number && (
          <div>Ref No: {fiscalInvoice.reference_number}</div>
        )}
      </div>

      {/* Invoice Type */}
      <div className="text-center border-t border-b border-dashed border-gray-400 py-1 mb-2 font-bold">
        {fiscalInvoice.invoice_type_name}
      </div>

      {/* Items */}
      <div className="mb-2">
        <div className="font-bold mb-1">Items</div>
        <div className="border-t border-gray-300"></div>
        <div className="grid grid-cols-12 text-[9px] font-semibold py-1">
          <div className="col-span-5">Name</div>
          <div className="col-span-2 text-right">Price</div>
          <div className="col-span-2 text-right">Qty</div>
          <div className="col-span-3 text-right">Total</div>
        </div>
        
        {fiscalInvoice.items?.map((item, idx) => (
          <div key={idx} className="mb-1">
            <div className="text-[10px]">
              {item.name} ({item.tax_label})
            </div>
            <div className="grid grid-cols-12 text-[9px]">
              <div className="col-span-5"></div>
              <div className="col-span-2 text-right">{item.price.toFixed(2)}</div>
              <div className="col-span-2 text-right">{item.quantity}</div>
              <div className="col-span-3 text-right">
                {fiscalInvoice.is_refund ? '-' : ''}{item.total.toFixed(2)}
              </div>
            </div>
          </div>
        ))}
        <div className="border-t border-gray-300 mt-1"></div>
      </div>

      {/* Totals */}
      <div className="mb-2">
        <div className="flex justify-between font-bold">
          <span>Total:</span>
          <span>{fiscalInvoice.total?.toFixed(2)}</span>
        </div>
        <div className="flex justify-between">
          <span>{fiscalInvoice.payment_method}:</span>
          <span>{fiscalInvoice.payment_amount?.toFixed(2)}</span>
        </div>
      </div>

      {/* Tax Breakdown */}
      <div className="border-t border-gray-300 pt-1 mb-2">
        <div className="grid grid-cols-12 text-[9px] font-semibold">
          <div className="col-span-2">Label</div>
          <div className="col-span-4">Name</div>
          <div className="col-span-3 text-right">Rate %</div>
          <div className="col-span-3 text-right">Tax</div>
        </div>
        {fiscalInvoice.tax_breakdown?.map((tax, idx) => (
          <div key={idx} className="grid grid-cols-12 text-[9px]">
            <div className="col-span-2">{tax.label}</div>
            <div className="col-span-4">{tax.name}</div>
            <div className="col-span-3 text-right">{tax.rate.toFixed(2)}</div>
            <div className="col-span-3 text-right">{tax.amount.toFixed(2)}</div>
          </div>
        ))}
        <div className="border-t border-dashed border-gray-400 mt-1 pt-1">
          <div className="flex justify-between font-bold text-[10px]">
            <span>Total Tax:</span>
            <span>{fiscalInvoice.total_tax?.toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Non-Fiscal Warning */}
      {isNonFiscal && (
        <div className="bg-yellow-100 border-2 border-yellow-500 text-yellow-800 text-center py-2 mb-2 font-bold text-sm">
          THIS IS NOT A FISCAL INVOICE
        </div>
      )}

      {/* SDC Metadata */}
      <div className="border-t-2 border-black pt-1 mb-2 text-[10px]">
        <div>SDC Time: {fiscalInvoice.sdc_time}</div>
        <div>SDC No: {fiscalInvoice.sdc_number}</div>
        <div>Invoice Counter: {fiscalInvoice.invoice_counter}</div>
      </div>

      {/* QR Code */}
      {fiscalInvoice.qr_code_base64 && (
        <div className="flex justify-center my-3">
          <img 
            src={`data:image/png;base64,${fiscalInvoice.qr_code_base64}`}
            alt="Verification QR Code"
            className="w-24 h-24"
          />
        </div>
      )}

      {/* End Line */}
      <div className="text-center border-t-2 border-b-2 border-black py-1">
        <div className="font-bold">END OF FISCAL INVOICE</div>
      </div>

      {/* Print Button */}
      {showPrintButton && onPrint && (
        <div className="mt-4 flex justify-center no-print">
          <Button onClick={onPrint} className="bg-teal-600 hover:bg-teal-700">
            <Printer className="w-4 h-4 mr-2" />
            Print Receipt
          </Button>
        </div>
      )}
    </div>
  );
};

// Fiscal Invoice Generator Hook
export const useFiscalInvoice = () => {
  const [loading, setLoading] = useState(false);
  const [fiscalInvoice, setFiscalInvoice] = useState(null);
  const [error, setError] = useState(null);

  const fiscalizeOrder = async (orderId, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (options.invoiceType) params.append('invoice_type', options.invoiceType);
      if (options.paymentMethod) params.append('payment_method', options.paymentMethod);
      if (options.cashierName) params.append('cashier_name', options.cashierName);

      const res = await api.post(`/frcs-vms/fiscalize-order/${orderId}?${params.toString()}`);

      const data = res.data;
      
      if (data.success) {
        setFiscalInvoice(data.fiscal_invoice);
        return data;
      } else {
        throw new Error(data.error || 'Fiscalization failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Fiscalization failed';
      setError(errorMessage);
      toast.error(`Fiscal Error: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const fiscalizeItems = async (items, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await api.post('/frcs-vms/fiscalize', {
        items: items.map(item => ({
          name: item.name,
          price: item.price,
          quantity: item.quantity || 1,
          tax_label: item.tax_label || 'G',
          gtin: item.gtin || null,
        })),
        payment_method: options.paymentMethod || 'CASH',
        payment_amount: options.paymentAmount,
        invoice_type: options.invoiceType || 'NS',
        cashier_name: options.cashierName || 'Admin',
        buyer_tin: options.buyerTin,
        reference_number: options.referenceNumber,
        order_id: options.orderId,
      });

      const data = res.data;
      
      if (data.success) {
        setFiscalInvoice(data.fiscal_invoice);
        return data;
      } else {
        throw new Error(data.error || 'Fiscalization failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Fiscalization failed';
      setError(errorMessage);
      toast.error(`Fiscal Error: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createRefund = async (originalSdcNumber, options = {}) => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (options.cashierName) params.append('cashier_name', options.cashierName);
      if (options.reason) params.append('reason', options.reason);

      const res = await api.post(`/frcs-vms/refund/${originalSdcNumber}?${params.toString()}`);

      const data = res.data;
      
      if (data.success) {
        setFiscalInvoice(data.fiscal_invoice);
        toast.success('Refund invoice created');
        return data;
      } else {
        throw new Error(data.error || 'Refund creation failed');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Refund creation failed';
      setError(errorMessage);
      toast.error(`Refund Error: ${errorMessage}`);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const clearFiscalInvoice = () => {
    setFiscalInvoice(null);
    setError(null);
  };

  return {
    loading,
    fiscalInvoice,
    error,
    fiscalizeOrder,
    fiscalizeItems,
    createRefund,
    clearFiscalInvoice,
  };
};

// Fiscal Status Badge Component
export const FiscalStatusBadge = ({ fiscalInvoice, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-blue-600">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Fiscalizing...</span>
      </div>
    );
  }

  if (!fiscalInvoice) {
    return (
      <div className="flex items-center gap-2 text-gray-400">
        <AlertTriangle className="w-4 h-4" />
        <span className="text-sm">Not Fiscalized</span>
      </div>
    );
  }

  const isNonFiscal = ['PS', 'PR', 'CS', 'CR', 'TS', 'TR'].includes(fiscalInvoice.invoice_type);

  return (
    <div className={`flex items-center gap-2 ${isNonFiscal ? 'text-yellow-600' : 'text-green-600'}`}>
      {isNonFiscal ? (
        <AlertTriangle className="w-4 h-4" />
      ) : (
        <Check className="w-4 h-4" />
      )}
      <span className="text-sm font-medium">
        {isNonFiscal ? 'Non-Fiscal' : 'Fiscalized'}
      </span>
      <span className="text-xs text-gray-500">
        {fiscalInvoice.sdc_number}
      </span>
    </div>
  );
};

// Escape HTML to prevent XSS
const escapeHtml = (str) => {
  if (str === null || str === undefined) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

// Print fiscal receipt function - sends to cloud print server
export const printFiscalReceipt = async (fiscalInvoice, orderId = null) => {
  if (!fiscalInvoice) {
    toast.error('No receipt data to print');
    return;
  }

  try {
    // Use API to create print job for the cloud print server
    const response = await api.post('/receipts/reprint-fiscal', {
      order_id: orderId || fiscalInvoice.order_id,
      station: 'customer',
      fiscal_invoice: fiscalInvoice
    });
    
    if (response.data?.success) {
      toast.success('Receipt sent to printer');
    } else {
      toast.info('Receipt queued for printing');
    }
  } catch (error) {
    console.error('Print error:', error);
    // Fallback - just show a toast, the receipt already printed on order completion
    toast.info('Receipt already sent to printer');
  }
};

export default FiscalReceiptDisplay;
